import { encodeCanonicalGs1Value } from './check-digit.js';
import { bytesToBase64Url, bytesToHex } from './epc.js';
const SHA256_K = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);
function rotr(n, x) {
    return (x >>> n) | (x << (32 - n));
}
function sha256BytesHex(bytes) {
    const bitLen = bytes.length * 8;
    const paddedLen = Math.ceil((bytes.length + 9) / 64) * 64;
    const padded = new Uint8Array(paddedLen);
    padded.set(bytes);
    padded[bytes.length] = 0x80;
    const view = new DataView(padded.buffer);
    view.setUint32(paddedLen - 8, Math.floor(bitLen / 0x100000000), false);
    view.setUint32(paddedLen - 4, bitLen >>> 0, false);
    let h0 = 0x6a09e667;
    let h1 = 0xbb67ae85;
    let h2 = 0x3c6ef372;
    let h3 = 0xa54ff53a;
    let h4 = 0x510e527f;
    let h5 = 0x9b05688c;
    let h6 = 0x1f83d9ab;
    let h7 = 0x5be0cd19;
    const w = new Uint32Array(64);
    for (let offset = 0; offset < paddedLen; offset += 64) {
        for (let i = 0; i < 16; i++) {
            w[i] = view.getUint32(offset + i * 4, false);
        }
        for (let i = 16; i < 64; i++) {
            const w15 = w[i - 15] ?? 0;
            const w2 = w[i - 2] ?? 0;
            const w16 = w[i - 16] ?? 0;
            const w7 = w[i - 7] ?? 0;
            const s0 = rotr(7, w15) ^ rotr(18, w15) ^ (w15 >>> 3);
            const s1 = rotr(17, w2) ^ rotr(19, w2) ^ (w2 >>> 10);
            w[i] = (w16 + s0 + w7 + s1) >>> 0;
        }
        let a = h0;
        let b = h1;
        let c = h2;
        let d = h3;
        let e = h4;
        let f = h5;
        let g = h6;
        let h = h7;
        for (let i = 0; i < 64; i++) {
            const S1 = rotr(6, e) ^ rotr(11, e) ^ rotr(25, e);
            const ch = (e & f) ^ (~e & g);
            const kVal = SHA256_K[i] ?? 0;
            const wVal = w[i] ?? 0;
            const temp1 = (h + S1 + ch + kVal + wVal) >>> 0;
            const S0 = rotr(2, a) ^ rotr(13, a) ^ rotr(22, a);
            const maj = (a & b) ^ (a & c) ^ (b & c);
            const temp2 = (S0 + maj) >>> 0;
            h = g;
            g = f;
            f = e;
            e = (d + temp1) >>> 0;
            d = c;
            c = b;
            b = a;
            a = (temp1 + temp2) >>> 0;
        }
        h0 = (h0 + a) >>> 0;
        h1 = (h1 + b) >>> 0;
        h2 = (h2 + c) >>> 0;
        h3 = (h3 + d) >>> 0;
        h4 = (h4 + e) >>> 0;
        h5 = (h5 + f) >>> 0;
        h6 = (h6 + g) >>> 0;
        h7 = (h7 + h) >>> 0;
    }
    return [h0, h1, h2, h3, h4, h5, h6, h7]
        .map((word) => word.toString(16).padStart(8, '0'))
        .join('');
}
/**
 * Pure TypeScript FIPS 180-4 SHA-256 synchronous implementation (browser + Node compatible,
 * zero `node:crypto` imports). Returns a 64-character lowercase hex digest.
 * Also exports `sha256HexSubtle` for async WebCrypto (`crypto.subtle.digest('SHA-256', ...)`).
 */
export function sha256Hex(input) {
    return sha256BytesHex(new TextEncoder().encode(input));
}
/**
 * Pure TypeScript FIPS 198-1 HMAC-SHA256 synchronous implementation (browser + Node compatible,
 * zero `node:crypto` imports). Returns a 64-character lowercase hex digest.
 */
export function hmacSha256Hex(key, message) {
    const encoder = new TextEncoder();
    let keyBytes = encoder.encode(key);
    if (keyBytes.length > 64) {
        const keyHashHex = sha256Hex(key);
        const hashed = new Uint8Array(32);
        for (let i = 0; i < 32; i++) {
            hashed[i] = Number.parseInt(keyHashHex.slice(i * 2, i * 2 + 2), 16);
        }
        keyBytes = hashed;
    }
    const blockKey = new Uint8Array(64);
    blockKey.set(keyBytes);
    const oKeyPad = new Uint8Array(64);
    const iKeyPad = new Uint8Array(64);
    for (let i = 0; i < 64; i++) {
        const b = blockKey[i] ?? 0;
        oKeyPad[i] = b ^ 0x5c;
        iKeyPad[i] = b ^ 0x36;
    }
    const msgBytes = encoder.encode(message);
    const innerInput = new Uint8Array(64 + msgBytes.length);
    innerInput.set(iKeyPad, 0);
    innerInput.set(msgBytes, 64);
    // Compute SHA-256 over raw bytes by encoding Latin1/binary or adding a bytes helper:
    const innerHex = sha256BytesHex(innerInput);
    const innerDigestBytes = new Uint8Array(32);
    for (let i = 0; i < 32; i++) {
        innerDigestBytes[i] = Number.parseInt(innerHex.slice(i * 2, i * 2 + 2), 16);
    }
    const outerInput = new Uint8Array(64 + 32);
    outerInput.set(oKeyPad, 0);
    outerInput.set(innerDigestBytes, 64);
    return sha256BytesHex(outerInput);
}
/**
 * Computes a lowercase hex SHA-256 digest using WebCrypto (`globalThis.crypto.subtle.digest('SHA-256', ...)`).
 */
export async function sha256HexSubtle(input) {
    const bytes = new TextEncoder().encode(input);
    const digestBuffer = await globalThis.crypto.subtle.digest('SHA-256', bytes);
    return bytesToHex(new Uint8Array(digestBuffer));
}
/**
 * Computes the deterministic Firestore qualifier document ID (Spec v2.1 §6.5):
 * URL-safe base64 of `ai=val&...` (or `sha256_<hex>` if >200 bytes), or `'BARE'` when empty.
 */
export function qualifierDocId(pairs) {
    if (pairs.length === 0) {
        return 'BARE';
    }
    const raw = pairs.map(([k, v]) => `${k}=${v}`).join('&');
    const b64 = bytesToBase64Url(new TextEncoder().encode(raw));
    return b64.length <= 200 ? b64 : `sha256_${sha256Hex(raw)}`;
}
/**
 * Computes the deterministic Firestore qualifier document ID from a combination code (`"BARE"`, `"22+10"`, etc.)
 * and a map of qualifier AI codes to values (Spec v2.1 §6.4).
 */
export function buildQualifierDocumentId(combinationCode, qualifiers) {
    if (combinationCode === 'BARE') {
        return 'BARE';
    }
    const aiOrder = combinationCode.split('+').filter((part) => part.length > 0);
    const pairs = [];
    for (const ai of aiOrder) {
        const val = qualifiers[ai];
        if (val !== undefined) {
            pairs.push([ai, val]);
        }
    }
    return qualifierDocId(pairs);
}
function makeCandidateLevel(combination, pairs) {
    const qualifierPath = pairs.map(([ai, val]) => `/${ai}/${encodeCanonicalGs1Value(val)}`).join('');
    return {
        docId: qualifierDocId(pairs),
        combination,
        qualifierPath,
        pairs,
    };
}
/**
 * Returns the ordered qualifier candidate levels from most specific to least specific (ending in BARE)
 * per GS1-Conformant Resolver Standard v1.2.1 §2.5.10 (official PDF p21–22, rules 1–4a–f) and Spec v2.1 §5.2.
 */
export function getCandidateQualifierLevels(dl) {
    const levels = [];
    const q = dl.qualifiers;
    if (dl.ai === '01' || dl.ai === '8006') {
        const s21 = q['21'];
        const t235 = q['235'];
        const c22 = q['22'];
        const l10 = q['10'];
        if (s21 !== undefined) {
            levels.push(makeCandidateLevel('21', [['21', s21]]));
        }
        if (t235 !== undefined) {
            levels.push(makeCandidateLevel('235', [['235', t235]]));
        }
        if (c22 !== undefined && l10 !== undefined) {
            levels.push(makeCandidateLevel('22_10', [
                ['22', c22],
                ['10', l10],
            ]));
        }
        if (l10 !== undefined) {
            levels.push(makeCandidateLevel('10', [['10', l10]]));
        }
        if (c22 !== undefined) {
            levels.push(makeCandidateLevel('22', [['22', c22]]));
        }
    }
    else if (dl.ai === '414') {
        const q254 = q['254'];
        const q7040 = q['7040'];
        if (q254 !== undefined) {
            levels.push(makeCandidateLevel('254', [['254', q254]]));
        }
        if (q7040 !== undefined) {
            levels.push(makeCandidateLevel('7040', [['7040', q7040]]));
        }
    }
    else if (dl.ai === '8010') {
        const q8011 = q['8011'];
        if (q8011 !== undefined) {
            levels.push(makeCandidateLevel('8011', [['8011', q8011]]));
        }
    }
    else if (dl.ai === '415') {
        const q8020 = q['8020'];
        if (q8020 !== undefined) {
            levels.push(makeCandidateLevel('8020', [['8020', q8020]]));
        }
    }
    else if (dl.ai === '417' || dl.ai === '8004') {
        const q7040 = q['7040'];
        if (q7040 !== undefined) {
            levels.push(makeCandidateLevel('7040', [['7040', q7040]]));
        }
    }
    else if (dl.ai === '8017' || dl.ai === '8018') {
        const q8019 = q['8019'];
        if (q8019 !== undefined) {
            levels.push(makeCandidateLevel('8019', [['8019', q8019]]));
        }
    }
    levels.push({
        docId: 'BARE',
        combination: 'BARE',
        qualifierPath: '',
        pairs: [],
    });
    return levels;
}
/**
 * Extracts typed path qualifier and secondary-key values from a parsed GS1 Digital Link
 * so downstream services never need raw AI string literals.
 * Key qualifiers are read exclusively from `dl.qualifiers` (URI path) per URI Syntax v1.7.0 §4.8, p25.
 */
export function extractScanQualifiers(dl) {
    const lot = dl.qualifiers['10'];
    const serial = dl.qualifiers['21'];
    const cpv = dl.qualifiers['22'];
    const tpx = dl.qualifiers['235'];
    const glnExtension = dl.qualifiers['254'];
    const secondaryGtin14 = dl.secondaryKeys['01'];
    return {
        ...(lot !== undefined ? { lot } : {}),
        ...(serial !== undefined ? { serial } : {}),
        ...(cpv !== undefined ? { cpv } : {}),
        ...(tpx !== undefined ? { tpx } : {}),
        ...(glnExtension !== undefined ? { glnExtension } : {}),
        ...(secondaryGtin14 !== undefined ? { secondaryGtin14 } : {}),
        isLocationGln: dl.ai === '414',
    };
}
//# sourceMappingURL=qualifiers.js.map